import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton,
  LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Pagination, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SvgIcon from '@mui/material/SvgIcon';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormClient from './FormClient';
import NfeItemTributosSection from './NfeItemTributosSection';
import {
  cancelarNfeGerencial,
  cartaCorrecaoNfe,
  downloadNfeGerencialPdf,
  downloadNfeGerencialXml,
  emitirNfeGerencial,
  enviarNfeGerencialEmail,
  enviarNfeGerencialWhatsApp,
  excluirNfeGerencialRascunho,
  getAllClients,
  getClientById,
  getAllPaymentMethods,
  getAllProducts,
  getAllTransportadoras,
  getCfopsFiscais,
  getEstabelecimentos,
  getNfeGerencial,
  getNfesGerenciais,
  getProductById,
  inutilizarNumeracaoFiscal,
  prepararNfeDeVenda,
  reenviarNfeGerencial,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import {
  formatBRLFromNumber,
  formatCurrencyInputBR,
  formatNfeItemMoedaForDisplay,
  parseBRLToNumberOrZero,
  parseNfeItemMoedaForSave,
  toMoedaInput,
} from '../utils/decimalBr';
import { getAliquotaInterestadual } from '../utils/fiscalConstants';
import { getFormaPagamentoLabel } from '../utils/fiscalLabels';
import {
  aplicarDefaultsTributos,
  defaultTributosExtras,
  mapTributosPayload,
  tributosFromGrupo,
} from '../utils/nfeItemTributos';

function WhatsAppIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </SvgIcon>
  );
}

const digitsPhone = (v) => String(v || '').replace(/\D/g, '').slice(0, 11);
const formatPhone = (v) => {
  const d = digitsPhone(v);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const MOD_FRETE = [
  { value: 0, label: '0 — CIF (emitente)' },
  { value: 1, label: '1 — FOB (destinatário)' },
  { value: 2, label: '2 — Terceiros' },
  { value: 3, label: '3 — Próprio remetente' },
  { value: 4, label: '4 — Próprio destinatário' },
  { value: 9, label: '9 — Sem frete' },
];

const emptyItem = () => ({
  produtoId: null,
  cProd: '',
  xProd: '',
  ncm: '',
  cest: '',
  cfop: '',
  uCom: 'UN',
  qCom: 1,
  vUnCom: 0,
  vDesc: 0,
  vOutro: 0,
  csosn: '102',
  origem: 0,
  cstIpi: 99,
  cstPis: 99,
  cstCofins: 99,
  ...defaultTributosExtras(),
  cstIbsCbs: '',
  cClassTrib: '',
  aliqIbsUf: '',
  aliqCbs: '',
  pDevol: '',
  vIPIDevol: '',
  vTotTrib: '',
});

const emptyEmit = () => ({
  id: null,
  numero: null,
  serie: null,
  vendaId: '',
  estabelecimentoId: '',
  cliente: null,
  natureza: 'Venda',
  finNFe: 1,
  tpNf: 1,
  idDest: 1,
  indFinal: 1,
  cfop: null,
  chavesReferenciadas: [],
  chaveNova: '',
  itens: [],
  pagamentos: [],
  descontoGeral: '',
  observacao: '',
  destacarImposto: true,
  nfeImportadaId: null,
  modFrete: 9,
  transportadora: null,
  placa: '',
  ufVeiculo: '',
  qtdVolumes: '',
  especieVolumes: '',
  pesoLiquido: '',
  pesoBruto: '',
});

const statusColor = (status) => {
  if (status === 'autorizada') return 'success';
  if (status === 'cancelada' || status === 'fiscal_rejeitada') return 'error';
  if (status === 'rascunho') return 'default';
  return 'warning';
};

const money = (v) => Number(parseBRLToNumberOrZero(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num = (v) => {
  if (v === '' || v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const brl = parseBRLToNumberOrZero(v);
  if (String(v).includes('R$') || String(v).includes(',')) return brl;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const itemTotal = (i) => Math.round((
  num(i.qCom) * parseBRLToNumberOrZero(i.vUnCom)
  - parseBRLToNumberOrZero(i.vDesc)
  + parseBRLToNumberOrZero(i.vOutro)
) * 100) / 100;

export default function NfeGerencialPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [emitForm, setEmitForm] = useState(emptyEmit());
  const [itemDlg, setItemDlg] = useState({ open: false, index: -1, item: emptyItem() });
  const [pagDlg, setPagDlg] = useState({ open: false, tPag: '01', vPag: '', forma: null });
  const [transportadoras, setTransportadoras] = useState([]);
  const [cfops, setCfops] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [estabs, setEstabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cancelDlg, setCancelDlg] = useState({ open: false, tipo: null, id: null, label: '' });
  const [excluirDlg, setExcluirDlg] = useState({ open: false, id: null, label: '' });
  const [justificativa, setJustificativa] = useState('');
  const [cceDlg, setCceDlg] = useState({ open: false, id: null, label: '', texto: '', sequencia: 1 });
  const [inutDlg, setInutDlg] = useState({
    open: false, estabelecimentoId: '', modelo: 65, serie: 1,
    numeroInicial: '', numeroFinal: '', justificativa: '',
  });
  const [clienteForm, setClienteForm] = useState({ open: false, id: null });
  const [rowMenu, setRowMenu] = useState({ anchor: null, item: null });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waNfeId, setWaNfeId] = useState(null);
  const [waTelefone, setWaTelefone] = useState('');
  const [waSaving, setWaSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailNfeId, setEmailNfeId] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [avancarDlg, setAvancarDlg] = useState({
    open: false, numero: '?', serie: '?', acao: null, item: null, apenasRascunho: false,
  });
  const [espelhoOpen, setEspelhoOpen] = useState(false);

  const isDevolucao = Number(emitForm.finNFe) === 4;

  const ufEmitente = useMemo(() => {
    const id = Number(emitForm.estabelecimentoId);
    return estabs.find((e) => e.id === id)?.estado || '';
  }, [emitForm.estabelecimentoId, estabs]);

  const ufDestinatario = emitForm.cliente?.estado || '';
  const emitenteSimples = useMemo(() => {
    const id = Number(emitForm.estabelecimentoId);
    const est = estabs.find((e) => e.id === id);
    return est?.simplesNacional !== false;
  }, [emitForm.estabelecimentoId, estabs]);
  const aliqInterestadual = useMemo(
    () => (Number(emitForm.idDest) === 2
      ? getAliquotaInterestadual(ufEmitente, ufDestinatario)
      : null),
    [emitForm.idDest, ufEmitente, ufDestinatario],
  );

  const totalItens = useMemo(
    () => emitForm.itens.reduce((s, i) => s + itemTotal(i), 0),
    [emitForm.itens],
  );
  const totalIpiDevol = useMemo(
    () => emitForm.itens.reduce((s, i) => s + parseBRLToNumberOrZero(i.vIPIDevol), 0),
    [emitForm.itens],
  );
  const totalNota = Math.round((totalItens - parseBRLToNumberOrZero(emitForm.descontoGeral) + totalIpiDevol) * 100) / 100;
  const totalPag = useMemo(
    () => emitForm.pagamentos.reduce((s, p) => s + num(p.vPag), 0),
    [emitForm.pagamentos],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getNfesGerenciais({ pagina, qtde: 20 });
      setItems(data.items || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao carregar NF-e.'));
    } finally {
      setLoading(false);
    }
  }, [pagina]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [eRes, cRes, fRes] = await Promise.all([
          getEstabelecimentos(),
          getCfopsFiscais({ pagina: 1, qtde: 500 }),
          getAllPaymentMethods(),
        ]);
        const listaE = Array.isArray(eRes.data) ? eRes.data : (eRes.data ? [eRes.data] : []);
        setEstabs(listaE.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Estab. ${e.id}`,
          estado: (e.estado || '').trim().toUpperCase(),
          simplesNacional: e.simplesNacional !== false,
        })));
        setCfops(cRes.data?.items || cRes.data || []);
        const fps = Array.isArray(fRes.data) ? fRes.data : (fRes.data?.items || []);
        setFormasPagamento(fps.map((f) => {
          const tPag = (f.referenciaSat ?? 0).toString().padStart(2, '0');
          return {
            id: f.id,
            label: `${f.descricao || getFormaPagamentoLabel(tPag)} — ${getFormaPagamentoLabel(tPag)}`,
            tPag,
            descricao: f.descricao,
          };
        }));
      } catch {
        /* ignore bootstrap errors */
      }

      // Prefill a partir de pedido comercial (ORC→PED→NF-e).
      try {
        const raw = sessionStorage.getItem('zen.nfe.prefill.pedidoComercial');
        if (!raw) return;
        sessionStorage.removeItem('zen.nfe.prefill.pedidoComercial');
        const payload = JSON.parse(raw);
        let cliente = null;
        if (payload.clienteId) {
          try {
            const { data: cli } = await getClientById(payload.clienteId);
            cliente = cli;
          } catch { /* ignore */ }
        }
        setEmitForm((prev) => ({
          ...prev,
          estabelecimentoId: payload.estabelecimentoId || prev.estabelecimentoId,
          cliente: cliente || prev.cliente,
          observacao: payload.codigo
            ? `Pedido comercial ${payload.codigo}`
            : prev.observacao,
          itens: (payload.itens || []).map((i) => ({
            ...emptyItem(),
            produtoId: i.produtoId,
            cProd: i.produtoCodigo || '',
            xProd: i.produtoDescricao || '',
            qCom: Number(i.quantidade) || 1,
            vUnCom: Number(i.valorUnitario) || 0,
            vDesc: Number(i.desconto) || 0,
          })),
        }));
      } catch {
        /* ignore prefill */
      }
    })();
  }, []);

  const aplicarCfop = (cfopObj, prev) => {
    if (!cfopObj) return prev;
    const interestadual = Number(prev.idDest) === 2;
    const codigo = interestadual && cfopObj.cfopInterestadual
      ? cfopObj.cfopInterestadual
      : cfopObj.codigo;
    return {
      ...prev,
      cfop: { ...cfopObj, codigo },
      natureza: cfopObj.naturezaOperacao || prev.natureza,
      finNFe: cfopObj.finalidadeNfe || prev.finNFe,
      tpNf: cfopObj.tipoNf ?? prev.tpNf,
      idDest: interestadual ? 2 : (cfopObj.destinoPadrao || prev.idDest),
      indFinal: cfopObj.consumidorFinal ? 1 : 0,
      itens: prev.itens.map((i) => ({ ...i, cfop: String(codigo) })),
    };
  };

  useEffect(() => {
    const d = location.state?.devolver;
    if (!d?.vendaId) return;
    (async () => {
      try {
        const { data } = await prepararNfeDeVenda(d.vendaId);
        const chave = (d.chaveReferenciada || '').replace(/\D/g, '');
        const ufO = (data.estabelecimentoEstado || '').trim().toUpperCase();
        const ufD = (data.cliente?.estado || '').trim().toUpperCase();
        const idDest = ufO && ufD && ufO !== ufD ? 2 : 1;
        setEmitForm({
          ...emptyEmit(),
          vendaId: String(d.vendaId),
          estabelecimentoId: String(data.estabelecimentoId || ''),
          cliente: data.cliente
            ? {
              id: data.cliente.id,
              label: `${data.cliente.razaoSocial || data.cliente.nomeFantasia || 'Cliente'} · ${data.cliente.cnpj || ''}`,
              estado: ufD,
            }
            : null,
          natureza: d.naturezaOperacao || 'Devolução de compra',
          finNFe: d.finNFe || 4,
          idDest,
          chavesReferenciadas: chave.length === 44 ? [chave] : [],
          nfeImportadaId: d.nfeImportadaId || null,
          descontoGeral: toMoedaInput(data.descontoGeral),
          itens: (data.itens || []).map((i) => ({
            ...emptyItem(),
            ...i,
            pDevol: i.pDevol ?? 100,
            vIPIDevol: i.vIPIDevol ?? '',
          })),
          pagamentos: [],
        });
        setOpen(true);
        setSuccess(`Devolução preparada a partir da venda #${d.vendaId}. Revise itens/refs e emita.`);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Falha ao preparar devolução.'));
      }
      navigate(location.pathname, { replace: true, state: {} });
    })();
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await getAllTransportadoras({ indice: 1, qtde: 100, ativo: true });
        setTransportadoras(
          (data.items || []).map((t) => ({
            id: t.id,
            label: `${t.razaoSocial || 'Transportadora'}${t.cnpj ? ` · ${t.cnpj}` : ''}`,
          })),
        );
      } catch {
        setTransportadoras([]);
      }
    })();
  }, [open]);

  const buscarClientes = async (texto) => {
    try {
      const { data } = await getAllClients({ indice: 1, qtde: 20, termo: texto || '' });
      setClientOptions((data.items || []).map((c) => ({
        id: c.id,
        label: `${c.razaoSocial || c.nomeFantasia || 'Cliente'}${c.cnpj ? ` · ${c.cnpj}` : ''}`,
        estado: (c.estado || '').trim().toUpperCase(),
        raw: c,
      })));
    } catch {
      setClientOptions([]);
    }
  };

  const selecionarClientePorId = async (id) => {
    if (!id) return;
    try {
      const { data: c } = await getClientById(id);
      const opt = {
        id: c.id,
        label: `${c.razaoSocial || c.nomeFantasia || 'Cliente'}${c.cnpj ? ` · ${c.cnpj}` : ''}`,
        estado: (c.estado || '').trim().toUpperCase(),
        raw: c,
      };
      setEmitForm((f) => ({ ...f, cliente: opt }));
      setClientOptions((prev) => {
        if (prev.some((x) => x.id === opt.id)) return prev;
        return [opt, ...prev];
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível recarregar o cliente.'));
    }
  };

  const aoSalvarCliente = async (_msg, savedId) => {
    const id = savedId || clienteForm.id || emitForm.cliente?.id;
    setClienteForm({ open: false, id: null });
    if (id) await selecionarClientePorId(id);
    setSuccess('Cliente salvo. Dados atualizados na nota.');
  };

  const buscarProdutos = async (texto) => {
    try {
      const { data } = await getAllProducts({ indice: 1, qtde: 20, termo: texto || '' });
      setProductOptions((data.items || []).map((p) => ({
        id: p.id,
        label: `${p.codigo || ''} — ${p.descricao || ''}`,
        raw: p,
      })));
    } catch {
      setProductOptions([]);
    }
  };

  const carregarVenda = async () => {
    if (!Number(emitForm.vendaId)) {
      setError('Informe o código da venda para carregar.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await prepararNfeDeVenda(Number(emitForm.vendaId));
      setEmitForm((f) => ({
        ...f,
        estabelecimentoId: String(data.estabelecimentoId || f.estabelecimentoId),
        cliente: data.cliente
          ? {
            id: data.cliente.id,
            label: `${data.cliente.razaoSocial || data.cliente.nomeFantasia || 'Cliente'} · ${data.cliente.cnpj || ''}`,
            estado: (data.cliente.estado || '').trim().toUpperCase(),
          }
          : f.cliente,
        descontoGeral: toMoedaInput(data.descontoGeral),
        itens: (data.itens || []).map((i) => ({ ...emptyItem(), ...i })),
        pagamentos: isDevolucao
          ? []
          : (data.pagamentos || []).map((p) => ({
            formaPagamentoId: p.formaPagamentoId,
            tPag: p.tPag,
            vPag: p.vPag,
            xPag: p.xPag,
          })),
      }));
      setSuccess(`Dados da venda #${data.vendaId} carregados. Você pode editar antes de emitir.`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar a venda.'));
    } finally {
      setSaving(false);
    }
  };

  const abrirItem = (index = -1) => {
    const base = index >= 0 ? { ...emitForm.itens[index] } : {
      ...emptyItem(),
      cfop: emitForm.cfop?.codigo ? String(emitForm.cfop.codigo) : '',
      pDevol: isDevolucao ? 100 : '',
    };
    if (isDevolucao && (base.pDevol === '' || base.pDevol == null)) {
      base.pDevol = 100;
    }
    setItemDlg({ open: true, index, item: formatNfeItemMoedaForDisplay(base) });
    setProductOptions([]);
  };

  const salvarItem = () => {
    const it = itemDlg.item;
    if (!it.xProd?.trim() || !it.ncm || num(it.qCom) <= 0) {
      setError('Item: informe descrição, NCM e quantidade.');
      return;
    }
    if (String(it.ncm).replace(/\D/g, '').length !== 8) {
      setError('NCM deve ter 8 dígitos.');
      return;
    }
    if (isDevolucao && parseBRLToNumberOrZero(it.vIPIDevol) > 0 && !(num(it.pDevol) > 0)) {
      setError('Com IPI devolvido, informe o percentual da mercadoria devolvida (ex.: 100 para devolução total do item).');
      return;
    }
    setEmitForm((f) => {
      const next = [...f.itens];
      const parsed = parseNfeItemMoedaForSave(it);
      const normalized = aplicarDefaultsTributos({
        ...parsed,
        qCom: num(parsed.qCom),
        origem: Number(parsed.origem) || 0,
        pDevol: parsed.pDevol === '' ? null : num(parsed.pDevol),
        vIPIDevol: parsed.vIPIDevol === '' ? null : parseBRLToNumberOrZero(parsed.vIPIDevol),
        vTotTrib: parsed.vTotTrib === '' ? null : parseBRLToNumberOrZero(parsed.vTotTrib),
        aliqIbsUf: parsed.aliqIbsUf === '' ? null : num(parsed.aliqIbsUf),
        aliqCbs: parsed.aliqCbs === '' ? null : num(parsed.aliqCbs),
      });
      if (itemDlg.index >= 0) next[itemDlg.index] = normalized;
      else next.push(normalized);
      return { ...f, itens: next };
    });
    setItemDlg({ open: false, index: -1, item: emptyItem() });
  };

  const adicionarChave = () => {
    const dig = emitForm.chaveNova.replace(/\D/g, '');
    if (dig.length !== 44) {
      setError('Chave referenciada deve ter 44 dígitos.');
      return;
    }
    if (emitForm.chavesReferenciadas.includes(dig)) {
      setError('Chave já adicionada.');
      return;
    }
    setEmitForm((f) => ({
      ...f,
      chavesReferenciadas: [...f.chavesReferenciadas, dig],
      chaveNova: '',
    }));
  };

  const adicionarPagamento = () => {
    const valor = parseBRLToNumberOrZero(pagDlg.vPag);
    if (!pagDlg.forma?.tPag) {
      setError('Selecione a forma de pagamento.');
      return;
    }
    if (valor <= 0) {
      setError('Informe um valor de pagamento válido.');
      return;
    }
    setEmitForm((f) => ({
      ...f,
      pagamentos: [
        ...f.pagamentos,
        {
          formaPagamentoId: pagDlg.forma.id,
          tPag: pagDlg.forma.tPag,
          vPag: valor,
          // SEFAZ 442: xPag só é permitido com tPag=99
          xPag: pagDlg.forma.tPag === '99' ? (pagDlg.forma.descricao || 'Outros') : null,
        },
      ],
    }));
    setPagDlg({ open: false, tPag: '01', vPag: '', forma: null });
  };

  const montarPayload = (apenasRascunho = false, avancarNumeracao = false) => ({
    id: emitForm.id || null,
    vendaId: Number(emitForm.vendaId) || null,
    estabelecimentoId: Number(emitForm.estabelecimentoId) || null,
    clienteId: emitForm.cliente?.id || null,
    naturezaOperacao: emitForm.natureza.trim() || (isDevolucao ? 'Devolução de compra' : 'Venda'),
    finNFe: Number(emitForm.finNFe) || 1,
    tpNf: Number(emitForm.tpNf),
    idDest: Number(emitForm.idDest),
    indFinal: Number(emitForm.indFinal),
    cfop: emitForm.cfop?.codigo || null,
    chavesReferenciadas: emitForm.chavesReferenciadas,
    nfeImportadaId: emitForm.nfeImportadaId || null,
    itens: emitForm.itens.map((i) => ({
      produtoId: i.produtoId,
      cProd: i.cProd,
      xProd: i.xProd,
      ncm: String(i.ncm).replace(/\D/g, ''),
      cest: i.cest || null,
      cfop: i.cfop || (emitForm.cfop?.codigo ? String(emitForm.cfop.codigo) : null),
      uCom: i.uCom || 'UN',
      qCom: num(i.qCom),
      vUnCom: num(i.vUnCom),
      vDesc: num(i.vDesc),
      vOutro: num(i.vOutro),
      csosn: i.csosn,
      origem: Number(i.origem) || 0,
      cstIbsCbs: i.cstIbsCbs || null,
      cClassTrib: i.cClassTrib || null,
      aliqIbsUf: i.aliqIbsUf === '' || i.aliqIbsUf == null ? null : num(i.aliqIbsUf),
      aliqCbs: i.aliqCbs === '' || i.aliqCbs == null ? null : num(i.aliqCbs),
      pDevol: i.pDevol == null || i.pDevol === '' ? null : num(i.pDevol),
      vIPIDevol: i.vIPIDevol == null || i.vIPIDevol === '' ? null : num(i.vIPIDevol),
      vTotTrib: i.vTotTrib == null || i.vTotTrib === '' ? null : num(i.vTotTrib),
      ...mapTributosPayload(i),
    })),
    pagamentos: isDevolucao
      ? [{ tPag: '90', vPag: 0 }]
      : emitForm.pagamentos.map((p) => {
        const tPag = String(p.tPag || '').padStart(2, '0');
        return {
          formaPagamentoId: p.formaPagamentoId,
          tPag,
          vPag: num(p.vPag),
          xPag: tPag === '99' ? (p.xPag || 'Outros') : null,
        };
      }),
    descontoGeral: parseBRLToNumberOrZero(emitForm.descontoGeral),
    observacao: emitForm.observacao.trim() || null,
    destacarImposto: true,
    apenasRascunho,
    avancarNumeracao: !!avancarNumeracao,
    modFrete: Number(emitForm.modFrete),
    transportadoraId: emitForm.transportadora?.id || null,
    placa: emitForm.placa.trim() || null,
    ufVeiculo: emitForm.ufVeiculo.trim() || null,
    qtdVolumes: emitForm.qtdVolumes === '' ? null : Number(emitForm.qtdVolumes),
    especieVolumes: emitForm.especieVolumes.trim() || null,
    pesoLiquido: emitForm.pesoLiquido === '' ? null : num(emitForm.pesoLiquido),
    pesoBruto: emitForm.pesoBruto === '' ? null : num(emitForm.pesoBruto),
  });

  const validarAntes = (apenasRascunho = false) => {
    if (!Number(emitForm.estabelecimentoId)) return 'Selecione o estabelecimento.';
    if (!emitForm.cliente?.id) return 'Selecione o cliente/destinatário.';
    if (emitForm.itens.length === 0) return 'Adicione ao menos um item.';
    if (isDevolucao && emitForm.chavesReferenciadas.length === 0) {
      return 'Devolução exige ao menos uma NF-e referenciada (44 dígitos).';
    }
    // Rascunho pode ficar sem pagamento; na emissão o total precisa fechar.
    if (!apenasRascunho && !isDevolucao && Math.abs(totalPag - totalNota) > 0.02) {
      return `Pagamentos (${money(totalPag)}) devem fechar com o total (${money(totalNota)}).`;
    }
    return '';
  };

  const isDuplicidadeFiscal = (msg) =>
    /\b(204|539)\b/.test(String(msg || '')) || /duplicidade/i.test(String(msg || ''));

  const executarEmissao = async (apenasRascunho, avancarNumeracao) => {
    setSaving(true);
    setError('');
    try {
      await emitirNfeGerencial(montarPayload(apenasRascunho, avancarNumeracao));
      setOpen(false);
      setEmitForm(emptyEmit());
      setSuccess(apenasRascunho ? 'Rascunho salvo.' : 'NF-e processada. Consulte o status na lista.');
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível emitir a NF-e.'));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const executarReenvio = async (item, avancarNumeracao) => {
    setError('');
    setSaving(true);
    try {
      await reenviarNfeGerencial(item.id, { avancarNumeracao });
      setSuccess(avancarNumeracao
        ? 'Numeração avançada. NF-e reenviada à SEFAZ.'
        : 'NF-e reenviada à SEFAZ.');
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível reenviar a NF-e.'));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const emitir = async (apenasRascunho = false) => {
    const msg = validarAntes(apenasRascunho);
    if (msg) {
      setError(msg);
      return;
    }
    if (!apenasRascunho) {
      setEspelhoOpen(true);
      return;
    }
    await executarEmissao(apenasRascunho, false);
  };

  const confirmarEspelhoEmitir = async () => {
    setEspelhoOpen(false);
    if (emitForm.id) {
      const atual = items.find((i) => i.id === emitForm.id);
      if (atual && isDuplicidadeFiscal(atual.mensagemFiscal)) {
        setAvancarDlg({
          open: true,
          numero: atual.numero || emitForm.numero || '?',
          serie: atual.serie || emitForm.serie || '?',
          acao: 'emitir',
          item: atual,
          apenasRascunho: false,
        });
        return;
      }
    }
    await executarEmissao(false, false);
  };

  const reenviar = async (itemOrId) => {
    const item = typeof itemOrId === 'object' && itemOrId
      ? itemOrId
      : items.find((i) => i.id === itemOrId) || { id: itemOrId };
    if (isDuplicidadeFiscal(item.mensagemFiscal)) {
      setAvancarDlg({
        open: true,
        numero: item.numero || '?',
        serie: item.serie || '?',
        acao: 'reenviar',
        item,
        apenasRascunho: false,
      });
      return;
    }
    await executarReenvio(item, false);
  };

  const fecharAvancarDlg = () => setAvancarDlg({
    open: false, numero: '?', serie: '?', acao: null, item: null, apenasRascunho: false,
  });

  const confirmarAvancarDlg = async () => {
    const { acao, item, apenasRascunho } = avancarDlg;
    fecharAvancarDlg();
    if (acao === 'emitir') await executarEmissao(apenasRascunho, true);
    else if (acao === 'reenviar' && item) await executarReenvio(item, true);
  };

  const podeEditar = (item) => item
    && item.statusFiscal !== 'autorizada'
    && item.statusFiscal !== 'cancelada';

  const abrirEdicao = async (item) => {
    setRowMenu({ anchor: null, item: null });
    setSaving(true);
    setError('');
    try {
      const { data } = await getNfeGerencial(item.id);
      const cfopObj = data.cfop
        ? (cfops.find((c) => c.codigo === data.cfop) || {
          codigo: data.cfop,
          descricao: String(data.cfop),
          naturezaOperacao: data.naturezaOperacao,
          finalidadeNfe: data.finNFe,
          tipoNf: data.tpNf,
          destinoPadrao: data.idDest,
          consumidorFinal: data.indFinal === 1,
        })
        : null;
      const cli = data.cliente;
      setEmitForm({
        ...emptyEmit(),
        id: data.id,
        numero: data.numero,
        serie: data.serie,
        vendaId: data.vendaId ? String(data.vendaId) : '',
        estabelecimentoId: String(data.estabelecimentoId || ''),
        cliente: cli
          ? {
            id: cli.id,
            label: `${cli.razaoSocial || cli.nomeFantasia || 'Cliente'}${cli.cnpj ? ` · ${cli.cnpj}` : ''}`,
          }
          : null,
        natureza: data.naturezaOperacao || 'Venda',
        finNFe: data.finNFe || 1,
        tpNf: data.tpNf ?? 1,
        idDest: data.idDest ?? 1,
        indFinal: data.indFinal ?? 1,
        cfop: cfopObj,
        chavesReferenciadas: data.chavesReferenciadas || [],
        descontoGeral: toMoedaInput(data.descontoGeral),
        observacao: data.observacao || '',
        destacarImposto: true,
        nfeImportadaId: data.nfeImportadaId || null,
        itens: (data.itens || []).map((i) => ({ ...emptyItem(), ...i })),
        pagamentos: (data.pagamentos || []).map((p) => ({
          formaPagamentoId: p.formaPagamentoId,
          tPag: String(p.tPag || '').padStart(2, '0'),
          vPag: p.vPag,
          xPag: p.xPag,
        })),
        modFrete: data.modFrete ?? 9,
        transportadora: data.transportadora || null,
        placa: data.placa || '',
        ufVeiculo: data.ufVeiculo || '',
        qtdVolumes: data.qtdVolumes ?? '',
        especieVolumes: data.especieVolumes || '',
        pesoLiquido: data.pesoLiquido ?? '',
        pesoBruto: data.pesoBruto ?? '',
      });
      if (cli) {
        setClientOptions([{
          id: cli.id,
          label: `${cli.razaoSocial || cli.nomeFantasia || 'Cliente'}${cli.cnpj ? ` · ${cli.cnpj}` : ''}`,
        }]);
      }
      setOpen(true);
      if (data.mensagemFiscal) {
        setError(`Última mensagem fiscal: ${data.mensagemFiscal}`);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível abrir a NF-e para edição.'));
    } finally {
      setSaving(false);
    }
  };

  const baixarPdf = async (item) => {
    try {
      const { data } = await downloadNfeGerencialPdf(item.id);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nfe-${item.numero || item.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'PDF ainda não disponível.'));
    }
  };

  const baixarXml = async (item) => {
    try {
      const { data } = await downloadNfeGerencialXml(item.id);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.chave ? `NFe${item.chave}.xml` : `nfe-${item.numero || item.id}.xml`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'XML ainda não disponível.'));
    }
  };

  const abrirDetalhe = async (item) => {
    if (!item?.id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setError('');
    try {
      const { data } = await getNfeGerencial(item.id);
      setDetail(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os detalhes da NF-e.'));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const abrirWhatsApp = async (nfeLike) => {
    setWaNfeId(nfeLike?.id);
    let tel = nfeLike?.cliente?.telefone || '';
    if (!tel && nfeLike?.id) {
      try {
        const { data } = await getNfeGerencial(nfeLike.id);
        tel = data?.cliente?.telefone || '';
        if (detailOpen && detail?.id === nfeLike.id) setDetail(data);
      } catch { /* ignore */ }
    }
    setWaTelefone(formatPhone(tel));
    setWaOpen(true);
  };

  const enviarWhatsApp = async () => {
    const tel = digitsPhone(waTelefone);
    if (tel.length < 10) {
      setError('Informe o WhatsApp com DDD.');
      return;
    }
    setWaSaving(true);
    setError('');
    try {
      await enviarNfeGerencialWhatsApp(waNfeId, tel, { incluirXml: true });
      setWaOpen(false);
      setSuccess('NF-e enviada pelo WhatsApp.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar pelo WhatsApp.'));
    } finally {
      setWaSaving(false);
    }
  };

  const abrirEmail = async (nfeLike) => {
    setEmailNfeId(nfeLike?.id);
    let email = (nfeLike?.cliente?.email || '').trim();
    if (!email && nfeLike?.id) {
      try {
        const { data } = await getNfeGerencial(nfeLike.id);
        email = (data?.cliente?.email || '').trim();
        if (detailOpen && detail?.id === nfeLike.id) setDetail(data);
      } catch { /* ignore */ }
    }
    setEmailTo(email);
    setEmailOpen(true);
  };

  const enviarEmail = async () => {
    const para = (emailTo || '').trim();
    if (para.length < 5 || !para.includes('@')) {
      setError('Informe um e-mail válido.');
      return;
    }
    setEmailSaving(true);
    setError('');
    try {
      await enviarNfeGerencialEmail(emailNfeId, { email: para });
      setEmailOpen(false);
      setSuccess(`NF-e enviada para ${para}.`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o e-mail.'));
    } finally {
      setEmailSaving(false);
    }
  };

  const confirmarCancelamento = async () => {
    const motivo = justificativa.trim();
    if (motivo.length < 15) {
      setError('Justificativa deve ter ao menos 15 caracteres.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await cancelarNfeGerencial(cancelDlg.id, motivo);
      setSuccess(`NF-e #${cancelDlg.id} cancelada.`);
      setCancelDlg({ open: false, tipo: null, id: null, label: '' });
      setJustificativa('');
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível cancelar.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmarExclusaoRascunho = async () => {
    if (!excluirDlg.id) return;
    setSaving(true);
    setError('');
    try {
      await excluirNfeGerencialRascunho(excluirDlg.id);
      setSuccess('Rascunho excluído.');
      setExcluirDlg({ open: false, id: null, label: '' });
      setRowMenu({ anchor: null, item: null });
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível excluir o rascunho.'));
    } finally {
      setSaving(false);
    }
  };

  const enviarCce = async () => {
    const texto = (cceDlg.texto || '').trim();
    if (texto.length < 15) {
      setError('Texto da CC-e deve ter ao menos 15 caracteres.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await cartaCorrecaoNfe(cceDlg.id, texto, Number(cceDlg.sequencia) || 1);
      setSuccess(`CC-e enviada para ${cceDlg.label}.`);
      setCceDlg({ open: false, id: null, label: '', texto: '', sequencia: 1 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível emitir a CC-e.'));
    } finally {
      setSaving(false);
    }
  };

  const enviarInutilizacao = async () => {
    const motivo = (inutDlg.justificativa || '').trim();
    if (!Number(inutDlg.estabelecimentoId)) {
      setError('Selecione o estabelecimento.');
      return;
    }
    if (motivo.length < 15) {
      setError('Justificativa deve ter ao menos 15 caracteres.');
      return;
    }
    if (!Number(inutDlg.numeroInicial) || !Number(inutDlg.numeroFinal)) {
      setError('Informe a numeração inicial e final.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await inutilizarNumeracaoFiscal({
        estabelecimentoId: Number(inutDlg.estabelecimentoId),
        modelo: Number(inutDlg.modelo),
        serie: Number(inutDlg.serie),
        numeroInicial: Number(inutDlg.numeroInicial),
        numeroFinal: Number(inutDlg.numeroFinal),
        justificativa: motivo,
      });
      setSuccess('Inutilização enviada à SEFAZ.');
      setInutDlg((d) => ({ ...d, open: false, justificativa: '', numeroInicial: '', numeroFinal: '' }));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível inutilizar a numeração.'));
    } finally {
      setSaving(false);
    }
  };

  const abrirInutilizacao = () => {
    setInutDlg((d) => ({
      ...d,
      open: true,
      estabelecimentoId: d.estabelecimentoId || (estabs[0] ? String(estabs[0].id) : ''),
    }));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Emitir NF-e"
        subtitle="Monte a NF-e do zero: itens, pagamentos, referências e impostos."
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<BlockIcon />} onClick={abrirInutilizacao}>
              Inutilizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEmitForm({
                  ...emptyEmit(),
                  estabelecimentoId: estabs[0] ? String(estabs[0].id) : '',
                });
                setOpen(true);
              }}
            >
              Nova NF-e
            </Button>
          </Stack>
        )}
      />
      {error && !open && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && !open && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Mensagem</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>Nenhuma NF-e emitida</TableCell></TableRow>
            ) : items.map((item) => (
              <TableRow
                key={item.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => abrirDetalhe(item)}
              >
                <TableCell data-label="Número">
                  {item.numero}/{item.serie}
                  {item.finNFe === 4 && <Chip size="small" label="Devolução" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell data-label="Origem">
                  {item.vendaId ? `Venda #${item.vendaId}` : 'Avulsa'}
                </TableCell>
                <TableCell data-label="Data">{new Date(item.dataCadastro).toLocaleString('pt-BR')}</TableCell>
                <TableCell data-label="Status">
                  <Chip size="small" color={statusColor(item.statusFiscal)} label={item.statusFiscal} />
                </TableCell>
                <TableCell data-label="Mensagem">{item.mensagemFiscal || '—'}</TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Ações">
                    <IconButton
                      onClick={(e) => setRowMenu({ anchor: e.currentTarget, item })}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
      <Stack alignItems="center" spacing={1} sx={{ mt: 2 }}>
        <Pagination count={totalPages} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="caption" color="text.secondary">Página {pagina} de {totalPages}</Typography>
      </Stack>

      <Menu
        anchorEl={rowMenu.anchor}
        open={Boolean(rowMenu.anchor)}
        onClose={() => setRowMenu({ anchor: null, item: null })}
      >
        <MenuItem onClick={() => {
          const it = rowMenu.item;
          setRowMenu({ anchor: null, item: null });
          abrirDetalhe(it);
        }}
        >
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Ver detalhes</ListItemText>
        </MenuItem>
        {podeEditar(rowMenu.item) && (
          <MenuItem onClick={() => abrirEdicao(rowMenu.item)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Editar / emitir</ListItemText>
          </MenuItem>
        )}
        {podeEditar(rowMenu.item) && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            reenviar(it);
          }}
          >
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Reenviar à SEFAZ</ListItemText>
          </MenuItem>
        )}
        {rowMenu.item?.statusFiscal === 'rascunho' && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            setExcluirDlg({
              open: true,
              id: it.id,
              label: `NF-e ${it.numero}/${it.serie}${it.vendaId ? ` (Venda #${it.vendaId})` : ''}`,
            });
          }}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Excluir rascunho</ListItemText>
          </MenuItem>
        )}
        {rowMenu.item?.statusFiscal === 'autorizada' && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            setCceDlg({
              open: true,
              id: it.id,
              label: `NF-e ${it.numero}/${it.serie}`,
              texto: '',
              sequencia: 1,
            });
          }}
          >
            <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Carta de correção (CC-e)</ListItemText>
          </MenuItem>
        )}
        {rowMenu.item?.statusFiscal === 'autorizada' && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            setJustificativa('');
            setCancelDlg({
              open: true,
              tipo: 'nfe',
              id: it.id,
              label: `NF-e ${it.numero}/${it.serie}`,
            });
          }}
          >
            <ListItemIcon><CancelOutlinedIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Cancelar NF-e</ListItemText>
          </MenuItem>
        )}
        {rowMenu.item?.temPdf && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            baixarPdf(it);
          }}
          >
            <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Baixar PDF (DANFE)</ListItemText>
          </MenuItem>
        )}
        {rowMenu.item?.temXml && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            baixarXml(it);
          }}
          >
            <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Baixar XML</ListItemText>
          </MenuItem>
        )}
        {(rowMenu.item?.temPdf || rowMenu.item?.statusFiscal === 'autorizada') && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            abrirWhatsApp(it);
          }}
          >
            <ListItemIcon><WhatsAppIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Enviar WhatsApp</ListItemText>
          </MenuItem>
        )}
        {(rowMenu.item?.temPdf || rowMenu.item?.temXml) && (
          <MenuItem onClick={() => {
            const it = rowMenu.item;
            setRowMenu({ anchor: null, item: null });
            abrirEmail(it);
          }}
          >
            <ListItemIcon><EmailOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Enviar e-mail</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={open}
        onClose={() => { setOpen(false); setError(''); }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {emitForm.id
            ? `Editar NF-e ${emitForm.numero}/${emitForm.serie}`
            : 'Montar NF-e'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            )}
            <Alert severity="info">
              Monte a nota completa (cabeçalho, itens, pagamentos/refs e frete). Opcional: carregar uma venda como base e editar.
            </Alert>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
              <TextField
                select
                label="Estabelecimento"
                value={emitForm.estabelecimentoId}
                onChange={(e) => setEmitForm((f) => ({ ...f, estabelecimentoId: e.target.value }))}
                required
                fullWidth
              >
                {estabs.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>{e.nome}</MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="flex-start">
                <Autocomplete
                  options={clientOptions}
                  value={emitForm.cliente}
                  onChange={(_e, value) => setEmitForm((f) => ({ ...f, cliente: value }))}
                  onInputChange={(_e, value, reason) => {
                    if (reason === 'input') buscarClientes(value);
                  }}
                  onOpen={() => { if (clientOptions.length === 0) buscarClientes(''); }}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente / destinatário *" required />
                  )}
                  sx={{ flex: 1 }}
                />
                <Tooltip title={emitForm.cliente?.id ? 'Editar cliente' : 'Novo cliente'}>
                  <span>
                    <IconButton
                      color="primary"
                      sx={{ mt: 0.5 }}
                      onClick={() => setClienteForm({
                        open: true,
                        id: emitForm.cliente?.id || null,
                      })}
                    >
                      {emitForm.cliente?.id ? <EditNoteIcon /> : <PersonAddAltIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                label="Código da venda (opcional)"
                type="number"
                value={emitForm.vendaId}
                onChange={(e) => setEmitForm((f) => ({ ...f, vendaId: e.target.value }))}
                fullWidth
              />
              <Button variant="outlined" onClick={carregarVenda} disabled={saving} sx={{ mt: { md: 1 } }}>
                Carregar venda
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Autocomplete
                options={cfops}
                value={emitForm.cfop}
                onChange={(_e, value) => setEmitForm((f) => aplicarCfop(value, f))}
                getOptionLabel={(o) => (o ? `${o.codigo} — ${o.descricao || o.naturezaOperacao || ''}` : '')}
                isOptionEqualToValue={(a, b) => a?.codigo === b?.codigo}
                renderInput={(params) => <TextField {...params} label="CFOP" />}
                fullWidth
              />
              <TextField
                label="Natureza da operação"
                value={emitForm.natureza}
                onChange={(e) => setEmitForm((f) => ({ ...f, natureza: e.target.value }))}
                required
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Finalidade"
                value={emitForm.finNFe}
                onChange={(e) => {
                  const fin = Number(e.target.value);
                  setEmitForm((f) => ({
                    ...f,
                    finNFe: fin,
                    natureza: fin === 4 && f.natureza === 'Venda' ? 'Devolução de compra' : f.natureza,
                  }));
                }}
                fullWidth
              >
                <MenuItem value={1}>1 — Normal</MenuItem>
                <MenuItem value={2}>2 — Complementar</MenuItem>
                <MenuItem value={3}>3 — Ajuste</MenuItem>
                <MenuItem value={4}>4 — Devolução</MenuItem>
              </TextField>
              <TextField
                select
                label="Tipo NF"
                value={emitForm.tpNf}
                onChange={(e) => setEmitForm((f) => ({ ...f, tpNf: Number(e.target.value) }))}
                fullWidth
              >
                <MenuItem value={0}>0 — Entrada</MenuItem>
                <MenuItem value={1}>1 — Saída</MenuItem>
              </TextField>
              <TextField
                select
                label="Destino"
                value={emitForm.idDest}
                onChange={(e) => {
                  const idDest = Number(e.target.value);
                  setEmitForm((f) => {
                    const next = { ...f, idDest };
                    return f.cfop ? aplicarCfop(f.cfop, next) : next;
                  });
                }}
                fullWidth
              >
                <MenuItem value={1}>1 — Interna</MenuItem>
                <MenuItem value={2}>2 — Interestadual</MenuItem>
                <MenuItem value={3}>3 — Exterior</MenuItem>
              </TextField>
              <TextField
                select
                label="Consumidor final"
                value={emitForm.indFinal}
                onChange={(e) => setEmitForm((f) => ({ ...f, indFinal: Number(e.target.value) }))}
                fullWidth
              >
                <MenuItem value={0}>0 — Não</MenuItem>
                <MenuItem value={1}>1 — Sim</MenuItem>
              </TextField>
            </Stack>

            {isDevolucao && (
              <Alert severity="warning">
                <strong>Devolução:</strong>
                {' '}
                informe a chave da NF de origem, CFOP de devolução (ex. 5202/6202) e, se a nota
                original tinha IPI agregando o total, preencha
                {' '}
                <strong>IPI devolvido</strong>
                {' '}
                no item — não use IPI próprio. O valor entra no total da NF. Informe também o
                percentual da mercadoria devolvida (100 = item inteiro).
              </Alert>
            )}
            {Number(emitForm.idDest) === 2 && aliqInterestadual != null && (
              <Alert severity="info">
                Operação interestadual ({ufEmitente || '?'} → {ufDestinatario || '?'}):
                alíquota interestadual sugerida <strong>{aliqInterestadual}%</strong> (DIFAL).
              </Alert>
            )}

            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Itens</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => abrirItem(-1)}>Adicionar item</Button>
            </Stack>
            <ResponsiveTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell>NCM</TableCell>
                    <TableCell>CFOP</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Unit.</TableCell>
                    {isDevolucao && <TableCell align="right">IPI devol.</TableCell>}
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emitForm.itens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isDevolucao ? 8 : 7} align="center">Nenhum item</TableCell>
                    </TableRow>
                  ) : emitForm.itens.map((it, idx) => (
                    <TableRow key={`${it.cProd}-${idx}`}>
                      <TableCell>{it.xProd}</TableCell>
                      <TableCell>{it.ncm}</TableCell>
                      <TableCell>{it.cfop || '—'}</TableCell>
                      <TableCell align="right">{it.qCom}</TableCell>
                      <TableCell align="right">{money(it.vUnCom)}</TableCell>
                      {isDevolucao && (
                        <TableCell align="right">{num(it.vIPIDevol) > 0 ? money(it.vIPIDevol) : '—'}</TableCell>
                      )}
                      <TableCell align="right">{money(itemTotal(it))}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => abrirItem(idx)}><EditNoteIcon fontSize="small" /></IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setEmitForm((f) => ({
                            ...f,
                            itens: f.itens.filter((_, i) => i !== idx),
                          }))}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTable>

            {isDevolucao ? (
              <>
                <Divider />
                <Typography variant="subtitle1">NF-e referenciadas</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <TextField
                    label="Chave (44 dígitos)"
                    value={emitForm.chaveNova}
                    onChange={(e) => setEmitForm((f) => ({ ...f, chaveNova: e.target.value }))}
                    fullWidth
                    inputProps={{ maxLength: 44 }}
                  />
                  <Button variant="outlined" onClick={adicionarChave}>Adicionar</Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {emitForm.chavesReferenciadas.map((c) => (
                    <Chip
                      key={c}
                      label={c}
                      onDelete={() => setEmitForm((f) => ({
                        ...f,
                        chavesReferenciadas: f.chavesReferenciadas.filter((x) => x !== c),
                      }))}
                    />
                  ))}
                </Stack>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pagamento: <strong>{getFormaPagamentoLabel('90')}</strong> (automático em notas de devolução).
                </Alert>
              </>
            ) : (
              <>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">Formas de pagamento</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setPagDlg({
                      open: true,
                      tPag: '01',
                      vPag: formatBRLFromNumber(Math.max(0, Math.round((totalNota - totalPag) * 100) / 100)),
                      forma: null,
                    })}
                  >
                    Adicionar
                  </Button>
                </Stack>
                {emitForm.pagamentos.map((p, idx) => (
                  <Stack key={`${p.tPag}-${idx}`} direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ flex: 1 }}>
                      {getFormaPagamentoLabel(p.tPag, p.xPag)}
                    </Typography>
                    <Typography>{money(p.vPag)}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setEmitForm((f) => ({
                        ...f,
                        pagamentos: f.pagamentos.filter((_, i) => i !== idx),
                      }))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Typography variant="body2" color={Math.abs(totalPag - totalNota) > 0.02 ? 'error' : 'text.secondary'}>
                  Pagamentos: {money(totalPag)} / Total nota: {money(totalNota)}
                </Typography>
              </>
            )}

            <Divider />
            <Typography variant="subtitle1">Frete / transporte</Typography>
            <TextField
              select
              label="Modalidade do frete"
              value={emitForm.modFrete}
              onChange={(e) => setEmitForm((f) => ({ ...f, modFrete: Number(e.target.value) }))}
              fullWidth
            >
              {MOD_FRETE.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
            <Autocomplete
              options={transportadoras}
              value={emitForm.transportadora}
              onChange={(_e, value) => setEmitForm((f) => ({ ...f, transportadora: value }))}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField {...params} label="Transportadora (opcional)" />
              )}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Placa"
                value={emitForm.placa}
                onChange={(e) => setEmitForm((f) => ({ ...f, placa: e.target.value.toUpperCase() }))}
                fullWidth
              />
              <TextField
                label="UF veículo"
                value={emitForm.ufVeiculo}
                onChange={(e) => setEmitForm((f) => ({ ...f, ufVeiculo: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 2 }}
                fullWidth
              />
              <TextField
                label="Qtd. volumes"
                type="number"
                value={emitForm.qtdVolumes}
                onChange={(e) => setEmitForm((f) => ({ ...f, qtdVolumes: e.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Espécie volumes"
                value={emitForm.especieVolumes}
                onChange={(e) => setEmitForm((f) => ({ ...f, especieVolumes: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Peso líquido"
                value={emitForm.pesoLiquido}
                onChange={(e) => setEmitForm((f) => ({ ...f, pesoLiquido: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Peso bruto"
                value={emitForm.pesoBruto}
                onChange={(e) => setEmitForm((f) => ({ ...f, pesoBruto: e.target.value }))}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Desconto geral"
                value={emitForm.descontoGeral}
                onChange={(e) => setEmitForm((f) => ({
                  ...f,
                  descontoGeral: formatCurrencyInputBR(e.target.value),
                }))}
                fullWidth
                inputMode="numeric"
                helperText="Digite só números; a vírgula entra sozinha."
              />
              <TextField
                label="Observações complementares"
                value={emitForm.observacao}
                onChange={(e) => setEmitForm((f) => ({ ...f, observacao: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>

            <Alert severity="success">
              Total dos itens: {money(totalItens)}
              {isDevolucao && totalIpiDevol > 0 ? ` · IPI devolvido: ${money(totalIpiDevol)}` : ''}
              {' · '}
              Desconto: {money(emitForm.descontoGeral)}
              {' · '}
              <strong>Total da nota: {money(totalNota)}</strong>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Fechar</Button>
          <Button onClick={() => emitir(true)} disabled={saving}>Salvar rascunho</Button>
          <Button variant="contained" onClick={() => emitir(false)} disabled={saving}>
            {saving ? 'Emitindo...' : 'Emitir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDlg.open} onClose={() => setItemDlg({ open: false, index: -1, item: emptyItem() })} fullWidth maxWidth="md">
        <DialogTitle>{itemDlg.index >= 0 ? 'Editar item' : 'Novo item'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Autocomplete
              options={productOptions}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') buscarProdutos(value);
              }}
              onOpen={() => { if (productOptions.length === 0) buscarProdutos(''); }}
              onChange={async (_e, value) => {
                if (!value?.raw) return;
                let p = value.raw;
                try {
                  const { data } = await getProductById(p.id);
                  if (data) p = data;
                } catch { /* use list payload */ }
                const g = p.grupoTributario || {};
                const precoVarejo = (p.precos || []).find((x) => x.tipo === 0 || x.tipo === 'Varejo')
                  || (p.precos || [])[0];
                setItemDlg((d) => ({
                  ...d,
                  item: {
                    ...d.item,
                    produtoId: p.id,
                    cProd: p.codigo || '',
                    xProd: p.descricao || '',
                    ncm: p.ncm || '',
                    cest: p.cest || '',
                    uCom: p.unidade || 'UN',
                    vUnCom: precoVarejo?.valor != null
                      ? formatBRLFromNumber(precoVarejo.valor)
                      : d.item.vUnCom,
                    cfop: g.cfop || d.item.cfop || (emitForm.cfop?.codigo ? String(emitForm.cfop.codigo) : ''),
                    csosn: g.cst != null ? String(g.cst).padStart(3, '0') : d.item.csosn,
                    origem: g.origem ?? d.item.origem,
                    ...tributosFromGrupo(g),
                    cstIpi: g.ipi ?? d.item.cstIpi,
                    cstPis: g.pis ?? d.item.cstPis,
                    cstCofins: g.cofins ?? d.item.cstCofins,
                    cstIbsCbs: g.cstIbsCbs || '',
                    cClassTrib: g.classTrib || '',
                    aliqIbsUf: g.aliquotaIbsUf ?? '',
                    aliqCbs: g.aliquotaCbs ?? '',
                  },
                }));
              }}
              getOptionLabel={(o) => o?.label || ''}
              renderInput={(params) => <TextField {...params} label="Buscar produto no cadastro" />}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Código"
                value={itemDlg.item.cProd}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, cProd: e.target.value } }))}
                fullWidth
              />
              <TextField
                label="Descrição"
                value={itemDlg.item.xProd}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, xProd: e.target.value } }))}
                required
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="NCM"
                value={itemDlg.item.ncm}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, ncm: e.target.value } }))}
                required
                fullWidth
              />
              <TextField
                label="CEST"
                value={itemDlg.item.cest}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, cest: e.target.value } }))}
                fullWidth
              />
              <TextField
                label="CFOP"
                value={itemDlg.item.cfop}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, cfop: e.target.value } }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Unidade"
                value={itemDlg.item.uCom}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, uCom: e.target.value } }))}
                fullWidth
              />
              <TextField
                label="Quantidade"
                value={itemDlg.item.qCom}
                onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, qCom: e.target.value } }))}
                fullWidth
              />
              <TextField
                label="Valor unitário"
                value={itemDlg.item.vUnCom}
                onChange={(e) => setItemDlg((d) => ({
                  ...d,
                  item: { ...d.item, vUnCom: formatCurrencyInputBR(e.target.value) },
                }))}
                fullWidth
                inputMode="numeric"
                helperText="Digite só números; a vírgula entra sozinha."
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Desconto"
                value={itemDlg.item.vDesc}
                onChange={(e) => setItemDlg((d) => ({
                  ...d,
                  item: { ...d.item, vDesc: formatCurrencyInputBR(e.target.value) },
                }))}
                fullWidth
                inputMode="numeric"
              />
              <TextField
                label="Acréscimo"
                value={itemDlg.item.vOutro}
                onChange={(e) => setItemDlg((d) => ({
                  ...d,
                  item: { ...d.item, vOutro: formatCurrencyInputBR(e.target.value) },
                }))}
                fullWidth
                inputMode="numeric"
              />
            </Stack>
            <NfeItemTributosSection
              item={itemDlg.item}
              onChange={(nextItem) => setItemDlg((d) => ({ ...d, item: nextItem }))}
              idDest={emitForm.idDest}
              ufEmitente={ufEmitente}
              ufDestinatario={ufDestinatario}
              simplesNacional={emitenteSimples}
              isDevolucao={isDevolucao}
            />
            {isDevolucao && (
              <Stack spacing={1.5}>
                <Alert severity="info">
                  IPI devolvido não é IPI de venda: informe o percentual devolvido e o valor
                  proporcional de IPI da NF de origem para o total fechar na SEFAZ.
                </Alert>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="% mercadoria devolvida"
                    value={itemDlg.item.pDevol}
                    onChange={(e) => setItemDlg((d) => ({ ...d, item: { ...d.item, pDevol: e.target.value } }))}
                    helperText="100 = item integral; parcial = proporção da qtd devolvida"
                    fullWidth
                  />
                  <TextField
                    label="Valor IPI devolvido"
                    value={itemDlg.item.vIPIDevol}
                    onChange={(e) => setItemDlg((d) => ({
                      ...d,
                      item: { ...d.item, vIPIDevol: formatCurrencyInputBR(e.target.value) },
                    }))}
                    helperText="Valor do IPI da origem (proporcional). Soma no total da NF."
                    fullWidth
                    inputMode="numeric"
                  />
                </Stack>
              </Stack>
            )}
            <Typography variant="body2">
              Total do item: {money(itemTotal(itemDlg.item))}
              {isDevolucao && parseBRLToNumberOrZero(itemDlg.item.vIPIDevol) > 0
                ? ` + IPI devol. ${money(itemDlg.item.vIPIDevol)} = ${money(itemTotal(itemDlg.item) + parseBRLToNumberOrZero(itemDlg.item.vIPIDevol))}`
                : ''}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDlg({ open: false, index: -1, item: emptyItem() })}>Cancelar</Button>
          <Button variant="contained" onClick={salvarItem}>Salvar item</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={espelhoOpen} onClose={() => setEspelhoOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Espelho da NF-e — conferir antes de emitir</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Revise os dados abaixo. Após confirmar, a nota será enviada à SEFAZ.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Destinatário</Typography>
                <Typography>{emitForm.cliente?.label || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Natureza / Finalidade</Typography>
                <Typography>
                  {emitForm.natureza || '—'}
                  {' · '}
                  finNFe={emitForm.finNFe}
                  {isDevolucao ? ' (Devolução)' : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">CFOP</Typography>
                <Typography>{emitForm.cfop?.codigo || emitForm.itens[0]?.cfop || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Destino</Typography>
                <Typography>
                  {Number(emitForm.idDest) === 2 ? 'Interestadual' : 'Interna'}
                  {aliqInterestadual != null ? ` · alíq. int. ${aliqInterestadual}%` : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography fontWeight={700}>{money(totalNota)}</Typography>
              </Grid>
            </Grid>
            {isDevolucao && emitForm.chavesReferenciadas.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">NF-e referenciada(s)</Typography>
                {emitForm.chavesReferenciadas.map((ch) => (
                  <Typography key={ch} variant="body2" sx={{ wordBreak: 'break-all' }}>{ch}</Typography>
                ))}
              </Box>
            )}
            <ResponsiveTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell>NCM</TableCell>
                    <TableCell>CFOP</TableCell>
                    <TableCell>CSOSN</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Unit.</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emitForm.itens.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{it.xProd}</TableCell>
                      <TableCell>{it.ncm || '—'}</TableCell>
                      <TableCell>{it.cfop}</TableCell>
                      <TableCell>{it.csosn}</TableCell>
                      <TableCell align="right">{it.qCom}</TableCell>
                      <TableCell align="right">{money(it.vUnCom)}</TableCell>
                      <TableCell align="right">{money(itemTotal(it))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTable>
            {!isDevolucao && emitForm.pagamentos.length > 0 && (
              <Box>
                <Typography variant="subtitle2">Pagamentos</Typography>
                {emitForm.pagamentos.map((p, i) => (
                  <Typography key={i} variant="body2">
                    {getFormaPagamentoLabel(p.tPag, p.xPag)}: {money(p.vPag)}
                  </Typography>
                ))}
              </Box>
            )}
            {isDevolucao && (
              <Typography variant="body2">
                Pagamento: {getFormaPagamentoLabel('90')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEspelhoOpen(false)}>Voltar e editar</Button>
          <Button variant="contained" onClick={confirmarEspelhoEmitir} disabled={saving}>
            {saving ? 'Emitindo...' : 'Confirmar e emitir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pagDlg.open} onClose={() => setPagDlg({ open: false, tPag: '01', vPag: '', forma: null })} fullWidth maxWidth="sm">
        <DialogTitle>Forma de pagamento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Autocomplete
              options={formasPagamento}
              value={pagDlg.forma}
              onChange={(_e, value) => setPagDlg((d) => ({ ...d, forma: value }))}
              getOptionLabel={(o) => o?.label || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => <TextField {...params} label="Forma" required />}
            />
            <TextField
              label="Valor"
              value={pagDlg.vPag}
              onChange={(e) => setPagDlg((d) => ({
                ...d,
                vPag: formatCurrencyInputBR(e.target.value),
              }))}
              required
              inputMode="numeric"
              helperText="Digite só números; a vírgula entra sozinha (ex.: 400 → R$ 4,00)."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagDlg({ open: false, tPag: '01', vPag: '', forma: null })}>Cancelar</Button>
          <Button variant="contained" onClick={adicionarPagamento}>Adicionar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={avancarDlg.open}
        onClose={fecharAvancarDlg}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Duplicidade de numeração</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              A SEFAZ rejeitou por duplicidade (204/539). A numeração
              {' '}
              <strong>{avancarDlg.numero}/{avancarDlg.serie}</strong>
              {' '}
              já foi utilizada anteriormente.
            </Alert>
            <Typography>
              Deseja avançar para o próximo número da sequência e reenviar a NF-e?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharAvancarDlg} disabled={saving}>Voltar</Button>
          <Button variant="contained" disabled={saving} onClick={confirmarAvancarDlg}>
            {saving ? 'Reenviando...' : 'Avançar numeração e reenviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={excluirDlg.open}
        onClose={() => setExcluirDlg({ open: false, id: null, label: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Excluir rascunho</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              Excluir o rascunho <strong>{excluirDlg.label}</strong>?
            </Typography>
            <Alert severity="warning">
              Esta nota ainda não foi emitida. Se for devolução de compra, a venda auxiliar
              deixará de aparecer no faturamento e você poderá preparar a devolução novamente.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcluirDlg({ open: false, id: null, label: '' })}>
            Voltar
          </Button>
          <Button color="error" variant="contained" disabled={saving} onClick={confirmarExclusaoRascunho}>
            {saving ? 'Excluindo...' : 'Excluir rascunho'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDlg.open}
        onClose={() => setCancelDlg({ open: false, tipo: null, id: null, label: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirmar cancelamento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>{cancelDlg.label}</Typography>
            <TextField
              label="Justificativa (mín. 15 caracteres)"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              multiline
              minRows={3}
              required
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDlg({ open: false, tipo: null, id: null, label: '' })}>
            Voltar
          </Button>
          <Button color="error" variant="contained" disabled={saving} onClick={confirmarCancelamento}>
            {saving ? 'Cancelando...' : 'Confirmar cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cceDlg.open}
        onClose={() => setCceDlg({ open: false, id: null, label: '', texto: '', sequencia: 1 })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Carta de correção</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>{cceDlg.label}</Typography>
            <Alert severity="info">
              A CC-e não altera valores, itens, destinatário nem datas. Use para correção textual (mín. 15 caracteres).
            </Alert>
            <TextField
              label="Sequência"
              type="number"
              value={cceDlg.sequencia}
              onChange={(e) => setCceDlg((d) => ({ ...d, sequencia: e.target.value }))}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Texto da correção"
              value={cceDlg.texto}
              onChange={(e) => setCceDlg((d) => ({ ...d, texto: e.target.value }))}
              multiline
              minRows={4}
              required
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCceDlg({ open: false, id: null, label: '', texto: '', sequencia: 1 })}>
            Fechar
          </Button>
          <Button variant="contained" disabled={saving} onClick={enviarCce}>
            {saving ? 'Enviando...' : 'Enviar CC-e'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inutDlg.open}
        onClose={() => setInutDlg((d) => ({ ...d, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Inutilizar numeração</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Inutiliza faixa de números não usados (NFC-e 65 ou NF-e 55). Justificativa mínima de 15 caracteres.
            </Alert>
            <TextField
              select
              label="Estabelecimento"
              value={inutDlg.estabelecimentoId}
              onChange={(e) => setInutDlg((d) => ({ ...d, estabelecimentoId: e.target.value }))}
              required
            >
              {estabs.map((e) => (
                <MenuItem key={e.id} value={String(e.id)}>{e.nome}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Modelo"
                value={inutDlg.modelo}
                onChange={(e) => setInutDlg((d) => ({ ...d, modelo: Number(e.target.value) }))}
                fullWidth
              >
                <MenuItem value={65}>65 — NFC-e</MenuItem>
                <MenuItem value={55}>55 — NF-e</MenuItem>
              </TextField>
              <TextField
                label="Série"
                type="number"
                value={inutDlg.serie}
                onChange={(e) => setInutDlg((d) => ({ ...d, serie: e.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Número inicial"
                type="number"
                value={inutDlg.numeroInicial}
                onChange={(e) => setInutDlg((d) => ({ ...d, numeroInicial: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Número final"
                type="number"
                value={inutDlg.numeroFinal}
                onChange={(e) => setInutDlg((d) => ({ ...d, numeroFinal: e.target.value }))}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Justificativa"
              value={inutDlg.justificativa}
              onChange={(e) => setInutDlg((d) => ({ ...d, justificativa: e.target.value }))}
              multiline
              minRows={3}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInutDlg((d) => ({ ...d, open: false }))}>Fechar</Button>
          <Button variant="contained" disabled={saving} onClick={enviarInutilizacao}>
            {saving ? 'Enviando...' : 'Inutilizar'}
          </Button>
        </DialogActions>
      </Dialog>

      <NfeDetailDialog
        open={detailOpen}
        loading={detailLoading}
        nfe={detail}
        onClose={() => setDetailOpen(false)}
        onPdf={() => detail && baixarPdf(detail)}
        onXml={() => detail && baixarXml(detail)}
        onWhatsApp={() => detail && abrirWhatsApp(detail)}
        onEmail={() => detail && abrirEmail(detail)}
        onEdit={() => {
          if (!detail) return;
          setDetailOpen(false);
          abrirEdicao(detail);
        }}
      />

      <Dialog open={waOpen} onClose={() => !waSaving && setWaOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Enviar NF-e no WhatsApp</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="WhatsApp (com DDD)"
            value={waTelefone}
            onChange={(e) => setWaTelefone(formatPhone(e.target.value))}
            fullWidth
            autoFocus
            placeholder="(11) 99999-9999"
            helperText="Envia o PDF (DANFE) e o XML, quando disponíveis."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaOpen(false)} disabled={waSaving}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={waSaving}
            onClick={enviarWhatsApp}
            startIcon={<WhatsAppIcon />}
          >
            {waSaving ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailOpen} onClose={() => !emailSaving && setEmailOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Enviar NF-e por e-mail</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="E-mail"
            type="email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            fullWidth
            autoFocus
            helperText="Anexa PDF e XML. Configure SMTP ou Gmail em Estabelecimentos → E-mail."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)} disabled={emailSaving}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={emailSaving}
            onClick={enviarEmail}
            startIcon={<EmailOutlinedIcon />}
          >
            {emailSaving ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <EntityFormDialog
        open={clienteForm.open}
        title={clienteForm.id ? 'Editar cliente' : 'Novo cliente'}
        subtitle={clienteForm.id ? `Código ${clienteForm.id}` : null}
        onClose={() => setClienteForm({ open: false, id: null })}
        maxWidth="lg"
        fullHeight
      >
        <FormClient
          key={clienteForm.id || 'novo-nfe'}
          embedded
          isEditMode={!!clienteForm.id}
          clientId={clienteForm.id}
          onCancel={() => setClienteForm({ open: false, id: null })}
          onSaved={aoSalvarCliente}
        />
      </EntityFormDialog>
    </Box>
  );
}

function NfeDetailDialog({
  open,
  loading,
  nfe,
  onClose,
  onPdf,
  onXml,
  onWhatsApp,
  onEmail,
  onEdit,
}) {
  const autorizada = nfe?.statusFiscal === 'autorizada';
  const clienteNome = nfe?.cliente
    ? (nfe.cliente.razaoSocial || nfe.cliente.nomeFantasia || `Cliente #${nfe.cliente.id}`)
    : '—';
  const itens = nfe?.itens || [];
  const pags = nfe?.pagamentos || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {nfe ? `NF-e ${nfe.numero}/${nfe.serie}` : 'Detalhes da NF-e'}
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress />}
        {!loading && nfe && (
          <Stack spacing={2}>
            <Alert severity={
              autorizada ? 'success'
                : nfe.statusFiscal === 'cancelada' || nfe.statusFiscal === 'fiscal_rejeitada' ? 'error'
                  : 'warning'
            }
            >
              Status: {nfe.statusFiscal}
              {nfe.chave ? ` · chave ${nfe.chave}` : ''}
              {nfe.protocolo ? ` · prot. ${nfe.protocolo}` : ''}
              {nfe.mensagemFiscal ? (
                <Box component="div" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{nfe.mensagemFiscal}</Box>
              ) : null}
            </Alert>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Data</Typography>
                <Typography>{nfe.dataCadastro ? new Date(nfe.dataCadastro).toLocaleString('pt-BR') : '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Destinatário</Typography>
                <Typography>{clienteNome}</Typography>
                {nfe.cliente?.cnpj && (
                  <Typography variant="body2" color="text.secondary">{nfe.cliente.cnpj}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Natureza</Typography>
                <Typography>{nfe.naturezaOperacao || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Origem</Typography>
                <Typography>{nfe.vendaId ? `Venda #${nfe.vendaId}` : 'Avulsa'}</Typography>
              </Grid>
              {nfe.cfop && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">CFOP</Typography>
                  <Typography>{nfe.cfop}</Typography>
                </Grid>
              )}
              {nfe.observacao && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Observação</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{nfe.observacao}</Typography>
                </Grid>
              )}
            </Grid>

            {itens.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>Itens</Typography>
                <ResponsiveTable>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell align="right">Qtd.</TableCell>
                        <TableCell align="right">Unitário</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itens.map((item, index) => {
                        const tot = Math.round((Number(item.qCom || 0) * Number(item.vUnCom || 0)
                          - Number(item.vDesc || 0) + Number(item.vOutro || 0)) * 100) / 100;
                        return (
                          <TableRow key={`${item.cProd}-${index}`}>
                            <TableCell data-label="Produto">
                              {item.cProd}<br />
                              <small>{item.xProd}</small>
                            </TableCell>
                            <TableCell align="right" data-label="Qtd.">
                              {Number(item.qCom || 0).toLocaleString('pt-BR')} {item.uCom || 'UN'}
                            </TableCell>
                            <TableCell align="right" data-label="Unitário">{money(item.vUnCom)}</TableCell>
                            <TableCell align="right" data-label="Total"><strong>{money(tot)}</strong></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ResponsiveTable>
              </Box>
            )}

            <Grid container spacing={2}>
              {pags.length > 0 && (
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle1" fontWeight={800} gutterBottom>Pagamentos</Typography>
                  <ResponsiveTable>
                    <Table size="small">
                      <TableBody>
                        {pags.map((p, index) => (
                          <TableRow key={`${p.tPag}-${index}`}>
                            <TableCell data-label="Forma">
                              {getFormaPagamentoLabel(p.tPag, p.xPag)}
                            </TableCell>
                            <TableCell align="right" data-label="Valor"><strong>{money(p.vPag)}</strong></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ResponsiveTable>
                </Grid>
              )}
              <Grid item xs={12} md={pags.length ? 5 : 12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={0.75}>
                    {Number(nfe.descontoGeral) > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography>Desconto</Typography>
                        <Typography color="error.main">-{money(nfe.descontoGeral)}</Typography>
                      </Stack>
                    )}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" fontWeight={900}>Total</Typography>
                      <Typography variant="h6" fontWeight={900}>{money(nfe.valorTotal)}</Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
        {nfe?.temPdf && (
          <Button startIcon={<PictureAsPdfIcon />} onClick={onPdf}>PDF</Button>
        )}
        {nfe?.temXml && (
          <Button startIcon={<CodeIcon />} onClick={onXml}>XML</Button>
        )}
        {(nfe?.temPdf || autorizada) && (
          <Button startIcon={<WhatsAppIcon />} onClick={onWhatsApp}>WhatsApp</Button>
        )}
        {(nfe?.temPdf || nfe?.temXml) && (
          <Button startIcon={<EmailOutlinedIcon />} onClick={onEmail}>E-mail</Button>
        )}
        {nfe && ['rascunho', 'fiscal_rejeitada', 'fiscal_pendente', 'processando'].includes(nfe.statusFiscal) && (
          <Button startIcon={<EditIcon />} onClick={onEdit}>Editar</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
